/**
 * Theme → image-style-prompt resolution, shared by the builder (apps/react)
 * and the server-side image generation path (apps/next, FEAT-042).
 *
 * Moved from `apps/react/src/utils/theme.ts` so the durable chat-to-proposal
 * workflow can derive the same style guidance the client's `generateImage`
 * call would have produced, without round-tripping presentation choices
 * through the API payload. Pure functions only — no DOM access.
 */

export type ThemeBackgroundType = 'solid' | 'gradient' | 'image';
export type ThemeAccentType = 'solid' | 'gradient';
export type ThemeOverlayEffect = 'frosted' | 'faded' | 'clear';
export type ThemeCardTransparency = 'default' | 'faded' | 'frosted' | 'glass';
export type ThemeAccentImageShape = 'default' | 'round' | 'inverse-round' | 'fade';
export type ThemeBlockFillColor = 'primary' | 'subtle';
export type ThemeBlockFillStyle = 'solid' | 'split' | 'outline';
export type ThemeButtonShape = 'square' | 'default' | 'round' | 'capsule';
export type GradientType = 'linear' | 'radial';

export interface PageBackground {
  type?: ThemeBackgroundType;
  imageUrl?: string;
  mediaType?: 'image' | 'video' | 'embed';
  colorCode?: string;
  useThemeCardColor?: boolean;
  accentColor?: string;
  accentType?: ThemeAccentType;
  gradient?: string;
  solidColor?: string;
  image_prompt?: string;
  overlayEffect?: ThemeOverlayEffect;
  image_generation_error?: string;
  image_generation_status?: 'failed';
  image_generation_request_id?: string;
  overlayIntensity?: number;
  overlayBlurIntensity?: number;
  overlayColor?: string;
}

export interface ThemeColorsFromBuilder {
  primary?: string;
  secondary?: string;
  heading?: string;
  body?: string;
  link?: string;
  button?: string;
  background?: {
    card?: PageBackground;
    page?: PageBackground;
  };
}

export interface ParsedGradient {
  colors: string[];
  positions?: number[];
  direction: string;
  type: GradientType;
}

export interface ActiveFontVariant {
  fontId: string;
  fontFamily: string;
  fallback?: string;
  weight: number;
  style: string;
  lineHeight?: number;
  letterSpacing?: number;
}

export interface BuilderTheme {
  containerWidth?: string;
  colors?: ThemeColorsFromBuilder;
  headingFonts?: ActiveFontVariant;
  bodyFonts?: ActiveFontVariant;
  cardRoundness?: string;
  cardShadow?: string;
  cardBorder?: string;
  cardTransparency?: ThemeCardTransparency;
  accentImageShape?: ThemeAccentImageShape;
  blockFillColor?: ThemeBlockFillColor;
  blockFillStyle?: ThemeBlockFillStyle;
  blockBorder?: string;
  blockShadow?: string;
  buttonShape?: ThemeButtonShape;
  imageStylePrompt?: string;
}

export const MAX_THEME_IMAGE_STYLE_PROMPT_LENGTH = 500;

// Page text sits on layout images, so any lettering in them competes with it.
// Image models drew theme names from the style prompt as large words, and they
// ignore negative prompts, so this is stated in the prompt itself.
export const withNoTextInImage = (prompt: string) =>
  `${prompt.trim().replace(/[.\s]+$/, '')}. The image has no text of any kind: no letters, words, numbers, logos, signs, labels, or watermarks.`;

export const normalizeHexColor = (color?: string) => {
  if (!color) {
    return undefined;
  }

  const trimmedColor = color.trim();
  if (!/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(trimmedColor)) {
    return undefined;
  }

  if (trimmedColor.length === 4) {
    return `#${trimmedColor[1]}${trimmedColor[1]}${trimmedColor[2]}${trimmedColor[2]}${trimmedColor[3]}${trimmedColor[3]}`.toLowerCase();
  }

  return trimmedColor.toLowerCase();
};

export const hexToRgb = (hexColor: string) => {
  const normalizedHexColor = normalizeHexColor(hexColor);

  if (!normalizedHexColor) {
    throw new Error(`Invalid hex color: ${hexColor}`);
  }

  return {
    r: parseInt(normalizedHexColor.slice(1, 3), 16),
    g: parseInt(normalizedHexColor.slice(3, 5), 16),
    b: parseInt(normalizedHexColor.slice(5, 7), 16),
  };
};

export const rgbToHex = ({ r, g, b }: { r: number; g: number; b: number }) =>
  `#${[r, g, b]
    .map((value) =>
      Math.max(0, Math.min(255, Math.round(value)))
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;

export const mixHexColors = (baseHex: string, mixHex: string, mixRatio: number) => {
  const baseRgb = hexToRgb(baseHex);
  const mixRgb = hexToRgb(mixHex);

  return rgbToHex({
    r: baseRgb.r * (1 - mixRatio) + mixRgb.r * mixRatio,
    g: baseRgb.g * (1 - mixRatio) + mixRgb.g * mixRatio,
    b: baseRgb.b * (1 - mixRatio) + mixRgb.b * mixRatio,
  });
};

export const hexToRgba = (hexColor: string, alpha: number) => {
  const { r, g, b } = hexToRgb(hexColor);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const rgbToHsl = ({ r, g, b }: { r: number; g: number; b: number }) => {
  const red = r / 255;
  const green = g / 255;
  const blue = b / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const lightness = (max + min) / 2;

  if (max === min) {
    return { h: 0, s: 0, l: lightness };
  }

  const delta = max - min;
  const saturation = lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);

  let hue = 0;
  if (max === red) {
    hue = (green - blue) / delta + (green < blue ? 6 : 0);
  } else if (max === green) {
    hue = (blue - red) / delta + 2;
  } else {
    hue = (red - green) / delta + 4;
  }

  return { h: hue / 6, s: saturation, l: lightness };
};

const hslToRgb = ({ h, s, l }: { h: number; s: number; l: number }) => {
  if (s === 0) {
    const channel = l * 255;
    return { r: channel, g: channel, b: channel };
  }

  const hueToRgb = (p: number, q: number, t: number) => {
    let normalizedT = t;
    if (normalizedT < 0) normalizedT += 1;
    if (normalizedT > 1) normalizedT -= 1;
    if (normalizedT < 1 / 6) return p + (q - p) * 6 * normalizedT;
    if (normalizedT < 1 / 2) return q;
    if (normalizedT < 2 / 3) return p + (q - p) * (2 / 3 - normalizedT) * 6;
    return p;
  };

  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;

  return {
    r: hueToRgb(p, q, h + 1 / 3) * 255,
    g: hueToRgb(p, q, h) * 255,
    b: hueToRgb(p, q, h - 1 / 3) * 255,
  };
};

export const adjustHexColor = (
  hexColor: string,
  adjustments: { lightness: number; saturation?: number },
) => {
  const hslColor = rgbToHsl(hexToRgb(hexColor));
  const nextSaturation = Math.max(0, Math.min(1, hslColor.s + (adjustments.saturation || 0)));
  const nextLightness = Math.max(0, Math.min(1, hslColor.l + adjustments.lightness));

  return rgbToHex(
    hslToRgb({
      h: hslColor.h,
      s: nextSaturation,
      l: nextLightness,
    }),
  );
};

export const isHexColorDark = (hexColor: string) => {
  const { r, g, b } = hexToRgb(hexColor);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.5;
};

const HUE_NAMES: Array<{ maxDeg: number; name: string }> = [
  { maxDeg: 15, name: 'red' },
  { maxDeg: 40, name: 'orange' },
  { maxDeg: 65, name: 'gold' },
  { maxDeg: 90, name: 'olive' },
  { maxDeg: 150, name: 'green' },
  { maxDeg: 175, name: 'teal' },
  { maxDeg: 200, name: 'cyan' },
  { maxDeg: 250, name: 'blue' },
  { maxDeg: 275, name: 'indigo' },
  { maxDeg: 310, name: 'violet' },
  { maxDeg: 345, name: 'magenta' },
  { maxDeg: 360, name: 'red' },
];

const describeHue = (hue: number) => {
  const deg = (((hue % 1) + 1) % 1) * 360;
  return HUE_NAMES.find((entry) => deg < entry.maxDeg)?.name ?? 'red';
};

/** Palette in words for image prompts. Never includes hex, names, or a medium. */
export const describeHexColor = (hexColor: string): string | undefined => {
  const normalized = normalizeHexColor(hexColor);
  if (!normalized) return undefined;

  const { h, s, l } = rgbToHsl(hexToRgb(normalized));
  if (s < 0.12) {
    if (l > 0.92) return 'white';
    if (l > 0.8) return 'ivory';
    if (l > 0.65) return 'pale grey';
    if (l > 0.45) return 'stone grey';
    if (l > 0.25) return 'charcoal';
    return 'near-black';
  }

  const hue = describeHue(h);
  if (l > 0.72 && s < 0.35) return `pale ${hue}`;
  if (l > 0.72) return `light ${hue}`;
  if (l < 0.32 && s < 0.35) return `deep muted ${hue}`;
  if (l < 0.32) return `deep ${hue}`;
  if (s < 0.35) return `muted ${hue}`;
  return hue;
};

const uniqueColorWords = (names: Array<string | undefined>) => {
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const name of names) {
    if (!name || seen.has(name)) continue;
    seen.add(name);
    unique.push(name);
  }
  return unique;
};

export type PaletteColorsForImageStyle = {
  primary?: string;
  secondary?: string;
  background?: {
    card?: { colorCode?: string };
    page?: { colorCode?: string };
  };
};

/**
 * One or two sentences of palette, light, and mood for a newly colored theme.
 * No hex, brand names, or medium, so it does not fight the document's image style.
 */
export const buildPaletteImageStylePrompt = (colors: PaletteColorsForImageStyle): string => {
  const card = colors.background?.card?.colorCode;
  const page = colors.background?.page?.colorCode;
  const dark = Boolean(card && isHexColorDark(card));
  const names = uniqueColorWords([
    describeHexColor(card ?? ''),
    describeHexColor(page ?? ''),
    describeHexColor(colors.primary ?? ''),
    describeHexColor(colors.secondary ?? ''),
  ]);

  const palette =
    names.length >= 2
      ? `${names[0]} surfaces with ${names.slice(1).join(' and ')} accents`
      : names.length === 1
        ? `${names[0]} surfaces and accents`
        : dark
          ? 'deep charcoal surfaces with a cool accent'
          : 'warm ivory surfaces with a muted accent';

  const light = dark
    ? 'Soft directional studio light, restrained highlights, deep mid-tones'
    : 'Soft natural window light, gentle shadows, generous negative space';
  const mood = dark ? 'Calm, precise, premium mood' : 'Calm, refined, open mood';
  const prompt = `${palette.charAt(0).toUpperCase()}${palette.slice(1)}. ${light}. ${mood}.`;

  if (prompt.length <= MAX_THEME_IMAGE_STYLE_PROMPT_LENGTH) return prompt;
  return `${prompt.slice(0, MAX_THEME_IMAGE_STYLE_PROMPT_LENGTH - 1).trimEnd()}.`;
};

/** Drops hex codes so a host-written mood cannot paint `#00f2fe` as lettering. */
export const sanitizeThemeImageStylePrompt = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const stripped = value
    .replace(/#[0-9a-fA-F]{3,8}\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (stripped.length < 24) return undefined;
  if (stripped.length <= MAX_THEME_IMAGE_STYLE_PROMPT_LENGTH) return stripped;
  return stripped.slice(0, MAX_THEME_IMAGE_STYLE_PROMPT_LENGTH).trimEnd();
};

export function parseGradientString(gradient: string): ParsedGradient | null {
  const normalizedGradient = gradient.trim();
  if (!normalizedGradient || !normalizedGradient.toLowerCase().includes('gradient(')) {
    return null;
  }

  const gradientMatch = normalizedGradient.match(/^(linear|radial)-gradient\s*\(([\s\S]+)\)$/i);
  if (!gradientMatch || !gradientMatch[1] || !gradientMatch[2]) {
    return null;
  }

  const splitGradientSegments = (value: string) => {
    const segments: string[] = [];
    let current = '';
    let depth = 0;

    for (const character of value) {
      if (character === '(') {
        depth += 1;
      } else if (character === ')') {
        depth = Math.max(depth - 1, 0);
      }

      if (character === ',' && depth === 0) {
        if (current.trim()) {
          segments.push(current.trim());
        }
        current = '';
        continue;
      }

      current += character;
    }

    if (current.trim()) {
      segments.push(current.trim());
    }

    return segments;
  };

  const parseColorStop = (segment: string) => {
    const positionMatch = segment.match(/\s(-?\d*\.?\d+)%\s*$/);
    const position = positionMatch ? Number(positionMatch[1]) : undefined;
    const color = (positionMatch ? segment.slice(0, positionMatch.index) : segment).trim();

    if (!color) {
      return null;
    }

    return { color, position };
  };

  const isColorStopSegment = (segment: string) => {
    const trimmedSegment = segment.trim().toLowerCase();
    return (
      trimmedSegment.startsWith('#') ||
      trimmedSegment.startsWith('rgb') ||
      trimmedSegment.startsWith('hsl') ||
      trimmedSegment.startsWith('oklch') ||
      trimmedSegment.startsWith('var(')
    );
  };

  const type = gradientMatch[1].toLowerCase() as GradientType;
  const inner = gradientMatch[2];
  const segments = splitGradientSegments(inner);

  const directionSegment = segments[0];
  let direction = '';
  let colorSegments = segments;

  if (directionSegment && !isColorStopSegment(directionSegment)) {
    direction = directionSegment;
    colorSegments = segments.slice(1);
  }

  const colors: string[] = [];
  const positions: number[] = [];

  for (const segment of colorSegments) {
    const stop = parseColorStop(segment);
    if (!stop) continue;
    colors.push(stop.color);
    if (stop.position !== undefined) {
      positions.push(stop.position);
    }
  }

  if (colors.length === 0) {
    return null;
  }

  return { colors, positions: positions.length > 0 ? positions : undefined, direction, type };
}

export const getGradientContrastSourceColor = (gradientColor?: string) => {
  if (!gradientColor) {
    return undefined;
  }

  const parsedGradient = parseGradientString(gradientColor);
  if (!parsedGradient?.colors?.length) {
    return undefined;
  }

  const gradientStops = parsedGradient.colors
    .map((color) => normalizeHexColor(color))
    .filter((color): color is string => Boolean(color));

  if (!gradientStops.length) {
    return undefined;
  }

  const averageColor = gradientStops.reduce(
    (accumulator, color) => {
      const { r, g, b } = hexToRgb(color);
      return {
        r: accumulator.r + r,
        g: accumulator.g + g,
        b: accumulator.b + b,
      };
    },
    { r: 0, g: 0, b: 0 },
  );

  return rgbToHex({
    r: averageColor.r / gradientStops.length,
    g: averageColor.g / gradientStops.length,
    b: averageColor.b / gradientStops.length,
  });
};

export const getAutoSecondaryColor = (cardColor?: string) => {
  const normalizedCardColor = normalizeHexColor(cardColor);

  if (!normalizedCardColor) {
    return '';
  }

  return isHexColorDark(normalizedCardColor)
    ? adjustHexColor(normalizedCardColor, { lightness: 0.07, saturation: 0.09 })
    : adjustHexColor(normalizedCardColor, { lightness: -0.05, saturation: 0.08 });
};

export const getContrastMixColor = (backgroundColor?: string) => {
  const normalizedBackgroundColor =
    normalizeHexColor(backgroundColor) || getGradientContrastSourceColor(backgroundColor);

  if (!normalizedBackgroundColor) {
    return undefined;
  }

  return isHexColorDark(normalizedBackgroundColor) ? 'white' : 'black';
};

const getTypedThemeOption = <T extends string>(
  value: string | undefined,
  allowedValues: readonly T[],
  fallback: T,
): T => (value && allowedValues.includes(value as T) ? (value as T) : fallback);

export const normalizeFontVariant = (font?: ActiveFontVariant): ActiveFontVariant | undefined => {
  if (!font) {
    return undefined;
  }

  return {
    ...font,
    fallback: font.fallback || 'sans-serif',
    lineHeight: font.lineHeight ?? 1.5,
    letterSpacing: font.letterSpacing ?? 0,
  };
};

export const normalizeBuilderTheme = (theme?: BuilderTheme): BuilderTheme => {
  const primaryColor = theme?.colors?.primary || '';
  const autoSecondaryColor = getAutoSecondaryColor(theme?.colors?.background?.card?.colorCode);
  const pageBackgroundType = getTypedThemeOption(
    theme?.colors?.background?.page?.type,
    ['solid', 'gradient', 'image'] as const,
    'solid',
  );
  const pageBackgroundGradient =
    theme?.colors?.background?.page?.gradient ||
    (pageBackgroundType === 'gradient' ? theme?.colors?.background?.page?.colorCode : '') ||
    '';

  return {
    ...theme,
    colors: {
      ...theme?.colors,
      primary: primaryColor,
      secondary: theme?.colors?.secondary || autoSecondaryColor,
      heading: theme?.colors?.heading || '',
      body: theme?.colors?.body || '',
      link: theme?.colors?.link || primaryColor,
      button: theme?.colors?.button || primaryColor,
      background: {
        ...theme?.colors?.background,
        card: {
          ...theme?.colors?.background?.card,
          type: theme?.colors?.background?.card?.type || 'solid',
          imageUrl: theme?.colors?.background?.card?.imageUrl || '',
          colorCode: theme?.colors?.background?.card?.colorCode || '',
        },
        page: {
          ...theme?.colors?.background?.page,
          type: pageBackgroundType,
          imageUrl: theme?.colors?.background?.page?.imageUrl || '',
          colorCode: theme?.colors?.background?.page?.colorCode || '',
          gradient: pageBackgroundGradient,
        },
      },
    },
    headingFonts: normalizeFontVariant(theme?.headingFonts),
    bodyFonts: normalizeFontVariant(theme?.bodyFonts),
    cardRoundness: theme?.cardRoundness || '0.5em',
    cardShadow: theme?.cardShadow || '0 2px 4px rgba(0,0,0,0.12)',
    cardBorder: theme?.cardBorder || '2px solid var(--th-primary-color)',
    cardTransparency: getTypedThemeOption(
      theme?.cardTransparency,
      ['default', 'faded', 'frosted', 'glass'] as const,
      'default',
    ),
    accentImageShape: getTypedThemeOption(
      theme?.accentImageShape,
      ['default', 'round', 'inverse-round', 'fade'] as const,
      'default',
    ),
    blockFillColor: getTypedThemeOption(
      theme?.blockFillColor,
      ['primary', 'subtle'] as const,
      'subtle',
    ),
    blockFillStyle: getTypedThemeOption(
      theme?.blockFillStyle,
      ['solid', 'split', 'outline'] as const,
      'solid',
    ),
    blockBorder: theme?.blockBorder || '2px solid var(--th-block-border-color)',
    blockShadow: theme?.blockShadow || '2px 2px 4px rgba(0,0,0,0.15)',
    buttonShape: getTypedThemeOption(
      theme?.buttonShape,
      ['square', 'default', 'round', 'capsule'] as const,
      'default',
    ),
    imageStylePrompt: theme?.imageStylePrompt || '',
  };
};

const joinPromptParts = (parts: string[]) => {
  if (parts.length === 0) {
    return '';
  }

  if (parts.length === 1) {
    return parts[0];
  }

  if (parts.length === 2) {
    return `${parts[0]} and ${parts[1]}`;
  }

  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
};

const getColorPalettePrompt = (theme: BuilderTheme) => {
  const { colors } = normalizeBuilderTheme(theme);
  const primaryColor = normalizeHexColor(colors?.primary);
  const secondaryColor = normalizeHexColor(colors?.secondary);
  const buttonColor = normalizeHexColor(colors?.button);
  const cardColor = normalizeHexColor(colors?.background?.card?.colorCode);
  const paletteParts = [
    primaryColor ? `primary ${primaryColor}` : '',
    secondaryColor && secondaryColor !== primaryColor ? `secondary ${secondaryColor}` : '',
    buttonColor && buttonColor !== primaryColor ? `button accents ${buttonColor}` : '',
    cardColor ? `surface tones ${cardColor}` : '',
  ].filter(Boolean);

  return paletteParts.length ? `a cohesive palette led by ${joinPromptParts(paletteParts)}` : '';
};

const getBackgroundPrompt = (theme: BuilderTheme) => {
  const pageBackground = normalizeBuilderTheme(theme).colors?.background?.page;

  if (pageBackground?.type === 'gradient') {
    return 'compositions that sit naturally on soft gradient backgrounds';
  }

  if (pageBackground?.type === 'image') {
    return 'imagery that integrates cleanly with image-backed layouts';
  }

  const pageColor = normalizeHexColor(pageBackground?.colorCode);
  return pageColor ? `background support colors anchored by ${pageColor}` : '';
};

const getSurfaceStylePrompt = (theme: BuilderTheme) => {
  const normalizedTheme = normalizeBuilderTheme(theme);
  const descriptors = [
    normalizedTheme.cardTransparency === 'glass'
      ? 'subtle glassmorphism'
      : normalizedTheme.cardTransparency === 'frosted'
        ? 'frosted translucent surfaces'
        : normalizedTheme.cardTransparency === 'faded'
          ? 'soft faded overlays'
          : '',
    normalizedTheme.accentImageShape === 'round'
      ? 'rounded organic image framing'
      : normalizedTheme.accentImageShape === 'inverse-round'
        ? 'inverted curved image framing'
        : normalizedTheme.accentImageShape === 'fade'
          ? 'soft edge-faded image blending'
          : '',
  ].filter(Boolean);

  return descriptors.length ? `with ${joinPromptParts(descriptors)}` : '';
};

export const buildThemeImageStylePrompt = (theme: BuilderTheme): string => {
  const promptParts = [
    'Create polished, presentation-ready visuals that match this theme',
    getColorPalettePrompt(theme),
    getBackgroundPrompt(theme),
    getSurfaceStylePrompt(theme),
    'clean composition, premium art direction, and consistent brand cohesion',
  ].filter(Boolean);

  const prompt = `${promptParts.join(', ')}.`;

  if (prompt.length <= MAX_THEME_IMAGE_STYLE_PROMPT_LENGTH) {
    return prompt;
  }

  const truncatedPrompt = prompt.slice(0, MAX_THEME_IMAGE_STYLE_PROMPT_LENGTH - 1).trimEnd();
  const lastWordBoundary = truncatedPrompt.lastIndexOf(' ');
  const cleanPrompt =
    lastWordBoundary > 0 ? truncatedPrompt.slice(0, lastWordBoundary) : truncatedPrompt;

  return `${cleanPrompt.trimEnd().replace(/[.,;:!?]+$/, '')}.`;
};

export const getResolvedThemeImageStylePrompt = (theme: BuilderTheme): string => {
  const normalizedTheme = normalizeBuilderTheme(theme);
  const customPrompt = normalizedTheme.imageStylePrompt?.trim();

  return customPrompt || buildThemeImageStylePrompt(normalizedTheme);
};

export const getThemeImageStylePromptError = (prompt?: string): string | null => {
  const trimmedPrompt = prompt?.trim() || '';

  if (!trimmedPrompt) {
    return 'Image style prompt';
  }

  if (trimmedPrompt.length > MAX_THEME_IMAGE_STYLE_PROMPT_LENGTH) {
    return `Image style prompt must be ${MAX_THEME_IMAGE_STYLE_PROMPT_LENGTH} characters or less`;
  }

  return null;
};
